import React, { useEffect } from "react";
import { cloneDeep, isEmpty } from "lodash";
import { useDispatch, useSelector } from "react-redux";
import { FieldArray, FormikProvider, useFormik } from "formik";

import {
  List,
  Button,
  Tooltip,
  Divider,
  useTheme,
  ListItem,
  IconButton,
  ListItemText,
  ListItemSecondaryAction,
} from "@mui/material";

import {
  Add as AddIcon,
  Edit as EditIcon,
  Clear as ClearIcon,
} from "@mui/icons-material";

import InitialView from "./InitialView";
import ReportNoteInput from "./ReportNoteInput";
import ReportBilling from "./billing/ReportBilling";
import usePdfViewer from "../../../pdf/viewer/usePdfViewer";
import AlertNotificationModal from "../modals/alert_notification/AlertNotificationModal";
import {
  NOTE_SAVE_HANDLERS,
  useRegisterNoteSaveHandler,
} from "../../NoteSaveHandlersContext";

import {
  changeBillableDate,
  setReportNoteEditable,
  setReportNotesTouched,
  setAutoScrollPageNumber,
  setExplicitReportNoteEditable,
} from "app/store/transmission/slice/transmissionSlice";

import formatDate from "utils/formatDate";
import { isSameDay } from "utils/date";
import { showErrorToast } from "utils/toast";
import { ReportNotesSchema } from "schemas/reportNoteSchema";
import moment from "moment";
import useNoteButtonActionInProgress from "../../footer/hooks/useNoteButtonActionInProgress";

const ReportNoteForm = ({
  rowData,
  reportNotes,
  reportNoteList,
  handleReportNote,
  handleReportNoteSaveFlow,
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();

  const {
    reportId,
    reportDetail,
    billableDate,
    currentPageNumber,
    originalBillableDate,
    isReportNoteEditable,
    hasReportNoteEditAccess,
    isAlertNotificationModal,
  } = useSelector((state) => state.transmission);

  const { handlePDFPageNavigation } = usePdfViewer({ reportId });
  const isActionInProgress = useNoteButtonActionInProgress();

  const formik = useFormik({
    initialValues: { reportNotes: reportNotes },
    initialErrors: {},
    enableReinitialize: true,
    validateOnChange: false,
    validationSchema: ReportNotesSchema,
  });

  const { values, errors, setFieldValue, validateForm } = formik;

  const whenSubmittable = (submit) => async () => {
    if (isActionInProgress) return;

    if (!isEmpty(await validateForm())) {
      showErrorToast("Please fix the errors in the report notes.");
      return;
    }

    submit();
  };

  const saveReportNotes = (options) =>
    handleReportNoteSaveFlow({
      notes: values,
      billingDate: billableDate,
      ...options,
    });

  useRegisterNoteSaveHandler(
    NOTE_SAVE_HANDLERS.REPORT_NOTE_SAVE,
    whenSubmittable(() => saveReportNotes())
  );

  useRegisterNoteSaveHandler(
    NOTE_SAVE_HANDLERS.REPORT_NOTE_SAVE_AND_PROCESS,
    whenSubmittable(() =>
      saveReportNotes({
        isProcessAction: true,
        isAlertNotificationSending: true,
      })
    )
  );

  useRegisterNoteSaveHandler(
    NOTE_SAVE_HANDLERS.REPORT_NOTE_UNSAVED,
    () => isReportNoteEditable && formik.dirty
  );

  useRegisterNoteSaveHandler(
    NOTE_SAVE_HANDLERS.BILLING_DATE_UNSAVED,
    () => isReportNoteEditable && !isSameDay(billableDate, originalBillableDate)
  );

  const reportNotesTouched = isReportNoteEditable && formik.dirty;
  useEffect(() => {
    dispatch(setReportNotesTouched(reportNotesTouched));
  }, [reportNotesTouched]);

  useEffect(() => () => dispatch(setReportNotesTouched(false)), []);

  useRegisterNoteSaveHandler(
    NOTE_SAVE_HANDLERS.REPORT_NOTE_ARCHIVE,
    whenSubmittable(() =>
      handleReportNote({
        notes: values,
        isProcessAction: true,
        isArchive: true,
      })
    )
  );

  const handleAddNote = (index) => {
    const reportNotesList = cloneDeep(values.reportNotes);

    const placeholder = reportNotesList[index].placeholders?.[0] || "";
    let newNote = { description: placeholder, alert: "None", pageNumber: null };

    const contents = reportNotesList[index]?.content || [];
    contents.unshift(newNote);
    reportNotesList[index].content = contents;

    setFieldValue("reportNotes", reportNotesList);
  };

  const renderReportNotesFooter = () => {
    if (!reportDetail) return null;

    const isArchived = reportDetail.evk_archived;

    const name = isArchived
      ? reportDetail.archived_by?.name
      : reportDetail.reportNoteUser?.name;

    const date = isArchived
      ? reportDetail.archived_at
      : reportDetail.reportNoteUser?.updated_at;

    const formattedDate = formatDate(date, "MM/DD/YYYY");
    const formattedTime = moment(date).local().format("hh:mm A");
    if (!name && !date) return null;

    return (
      <span style={{ fontSize: "12px" }}>
        <p className="mt-2">
          {isArchived && name && <>Report Archived by </>}
          {name && <span style={{ fontWeight: 500 }}>{name}</span>}
        </p>
        <p style={{ color: theme.palette.text.secondary, marginTop: "4px" }}>
          {formattedDate && <span>{formattedDate}</span>}
          {formattedTime && (
            <>
              {" "}
              • <span>{formattedTime}</span>
            </>
          )}
        </p>
      </span>
    );
  };

  return (
    <FormikProvider value={formik}>
      <form>
        <div className="text-normal">{rowData?.reportTitle}</div>

        <InitialView handlePDFPageNavigation={handlePDFPageNavigation} />

        <List>
          {values.reportNotes?.map((reportNote, index) => {
            return (
              <List style={{ padding: "2px 0", marginTop: "10px" }} key={index}>
                <ListItem
                  disableGutters
                  className="report-note-item"
                  style={{
                    backgroundColor:
                      theme.palette.type === "dark" ? "#333333" : "#f5f5f5",
                    borderColor:
                      theme.palette.type === "dark" ? "#444444" : "#E0E0E0",
                  }}
                >
                  <ListItemText
                    primary={reportNote?.note}
                    primaryTypographyProps={{ style: { fontWeight: 500 } }}
                  />
                  {isReportNoteEditable && (
                    <Tooltip title="Add new note">
                      <ListItemSecondaryAction
                        onClick={() => handleAddNote(index)}
                      >
                        <IconButton edge="end" aria-label="add">
                          <AddIcon />
                        </IconButton>
                      </ListItemSecondaryAction>
                    </Tooltip>
                  )}
                </ListItem>
                <FieldArray
                  name={`reportNotes[${index}].content`}
                  validateOnChange={false}
                  render={(arrayHelpers) => (
                    <div style={{ maxHeight: "200px", overflowY: "auto" }}>
                      {reportNote.content?.map((note, noteIndex) => (
                        <>
                          {noteIndex !== 0 && (
                            <Divider style={{ margin: "4px 0" }} />
                          )}

                          <ReportNoteInput
                            key={noteIndex}
                            note={note}
                            errors={errors}
                            noteIndex={noteIndex}
                            reportNote={reportNote}
                            reportNoteIndex={index}
                            arrayHelpers={arrayHelpers}
                            setFieldValue={setFieldValue}
                            currentPageNumber={currentPageNumber}
                            navigateToPdfPage={handlePDFPageNavigation}
                            isReportNoteEditable={isReportNoteEditable}
                          />
                        </>
                      ))}
                    </div>
                  )}
                />
              </List>
            );
          })}
        </List>

        <Divider style={{ margin: "8px 0" }} />

        <ReportBilling data={rowData} />

        <div className="d-flex justify-between align-items-center">
          <div style={{ fontWeight: 400, fontSize: "12px" }}>
            {renderReportNotesFooter()}
          </div>
          {hasReportNoteEditAccess && (
            <div className="d-flex flex-end">
              {isAlertNotificationModal && (
                <AlertNotificationModal
                  notes={values}
                  open={isAlertNotificationModal}
                  handleReportNote={handleReportNote}
                />
              )}

              {isReportNoteEditable ? (
                <Button
                  onClick={() => {
                    setFieldValue("reportNotes", reportNoteList);
                    dispatch(setReportNoteEditable(false));
                    dispatch(changeBillableDate(originalBillableDate));
                    dispatch(
                      setAutoScrollPageNumber(
                        reportDetail?.auto_scroll_page_number
                      )
                    );
                  }}
                  className="view-pdf__button"
                  variant="text"
                  startIcon={<ClearIcon />}
                  disabled={isActionInProgress}
                >
                  <div style={{ fontWeight: 700, fontSize: "12px" }}>
                    Cancel Editing
                  </div>
                </Button>
              ) : (
                <Button
                  onClick={() => {
                    dispatch(setReportNoteEditable(true));
                    dispatch(setExplicitReportNoteEditable(true));
                  }}
                  className="view-pdf__button"
                  variant="text"
                  color="secondary"
                  startIcon={<EditIcon data-action="edit-report-note-icon" />}
                  disabled={isActionInProgress}
                >
                  <div style={{ fontWeight: 700, fontSize: "12px" }}>Edit</div>
                </Button>
              )}
            </div>
          )}
        </div>
      </form>
    </FormikProvider>
  );
};

export default ReportNoteForm;
